import React, { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaw,
  faMapMarkerAlt,
  faPhoneAlt,
  faTimesCircle,
  faClock,
  faInfoCircle,

} from "@fortawesome/free-solid-svg-icons";
import WalkMapStatus from "./WalkStatus";
import { CURRENCY_CODE, WALK_STATUS } from "../../../config/constants";
import { motion } from "framer-motion";
import EditAdditionalInfo from "../../../components/AdditionalInfoEditor";
import Feedback from "./Feedback";
import DurationTimer from "./DurationTimer";

const WalkDetailModal = ({ walk, isOpen, onClose }) => {
  const [shouldFetch, setShouldFetch] = useState(false);


  const handleAdditionalInfoSave = (shouldFetch = false) => {
    setShouldFetch(shouldFetch);
  };

  if (!walk) return null;

  const {
    status,
    duration,
    clean_pet,
    emergency_no,
    cancel_reason,
    scheduled_at,
    additional_info,
    charges,
    walker,
    promo,
    pickup_location,
    pets,
    tools,
    status_time,
  } = walk;

  const getServiceLabel = (key) => {
    switch (key) {
      case "duration":
        return "Pet Walk Duration";
      case "pet":
        return "Additional Pet Fee";
      case "tool":
        return "Pet Walker’s Gear & Supplies";
      default:
        return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
    }
  };

  const canEditAdditionalDetail = () => {
    if (
      status == WALK_STATUS.REQUESTED ||
      status == WALK_STATUS.FINDING_WALKER
    ) {
      return true;
    }
    return false;
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => onClose(shouldFetch)}
      >
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-center justify-center min-h-full p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-sm sm:max-w-2xl lg:max-w-4xl xl:max-w-7xl transform overflow-hidden rounded-xl sm:rounded-2xl bg-white p-3 sm:p-4 lg:p-6 text-left align-middle shadow-xl transition-all max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex justify-between items-center mb-3 sm:mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900"
                  >
                    Walk Details
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 transition-transform transform p-1"
                  >
                    <FontAwesomeIcon icon={faTimesCircle} className="w-5 h-5 sm:w-6 sm:h-6" />
                  </button>
                </div>

                {/* Status Badge */}

                {/* Conditional Walk Completion */}
                <WalkMapStatus walk={walk} />
                {status == WALK_STATUS.IN_PROGRESS && (
                  <>
                    <DurationTimer start_time={status_time[WALK_STATUS.IN_PROGRESS]} duration={duration} />
                  </>
                )}

                {status == WALK_STATUS.COMPLETED && walk.feedback == null && (
                  <>
                    <Feedback type={"walk"} item={walk} />
                  </>
                )}

                {/* Clean Pet Option */}
                <div className="flex items-center space-x-2 text-gray-700 mt-6">
                  <FontAwesomeIcon
                    icon={faPaw}
                    className={clean_pet ? "text-green-600" : "text-gray-400"}
                  />
                  <span>
                    {clean_pet
                      ? "Pet Cleaning Included"
                      : "Pet Cleaning Not Included"}
                  </span>
                </div>

                {/* Main Details Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 mt-4 sm:mt-6">
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex items-start text-gray-700">
                      <FontAwesomeIcon
                        icon={faMapMarkerAlt}
                        className="text-purple-600 mr-2 mt-0.5 flex-shrink-0"
                      />
                      <span className="text-sm sm:text-base">
                        <span className="font-medium">Pickup Location:</span> {pickup_location?.address || "N/A"}
                      </span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <FontAwesomeIcon
                        icon={faClock}
                        className="text-purple-600 mr-2 flex-shrink-0"
                      />
                      <span className="text-sm sm:text-base">
                        <span className="font-medium">Duration:</span> {duration || "N/A"}
                      </span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <FontAwesomeIcon
                        icon={faPhoneAlt}
                        className="text-purple-600 mr-2 flex-shrink-0"
                      />
                      <span className="text-sm sm:text-base">
                        <span className="font-medium">Emergency Contact:</span> {emergency_no || "N/A"}
                      </span>
                    </div>
                    {scheduled_at && (
                      <div className="text-gray-700">
                        <div className="flex items-start">
                          <FontAwesomeIcon
                            icon={faClock}
                            className="text-purple-600 mr-2 mt-0.5 flex-shrink-0"
                          />
                          <div>
                            <span className="text-sm sm:text-base">
                              <span className="font-medium">Scheduled At:</span>{" "}
                              {new Date(scheduled_at).toLocaleString()}
                            </span>
                            <p className="text-xs sm:text-sm text-gray-600 mt-1">
                              <strong>Note:</strong> We'll send you an email one
                              hour before the scheduled time.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Walker Info */}
                  {walker && (
                    <div className="flex items-start p-3 sm:p-4 rounded-lg bg-gray-50 shadow-md">
                      <img
                        src={walker.profile_photo}
                        alt={walker.name}
                        className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover shadow-sm flex-shrink-0"
                      />
                      <div className="ml-3 sm:ml-4 text-gray-700 space-y-1">
                        <h4 className="text-base sm:text-lg font-semibold">{walker.name}</h4>
                        <p className="text-sm sm:text-base">
                          <FontAwesomeIcon
                            icon={faPhoneAlt}
                            className="mr-2 text-purple-500 text-xs sm:text-sm"
                          />
                          {walker.phone || "N/A"}
                        </p>
                        <p className="text-sm sm:text-base">
                          <FontAwesomeIcon
                            icon={faInfoCircle}
                            className="mr-2 text-purple-500 text-xs sm:text-sm"
                          />
                          {walker.email || "N/A"}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Charges Section */}
                <div className="bg-gray-100 p-3 sm:p-4 rounded-lg shadow-md mt-4 sm:mt-6">
                  <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Cost Breakdown</h2>
                  <div className="space-y-2">
                    {Object.keys(charges).map((key, index) =>
                      key !== "base_rate" &&
                        key !== "total" &&
                        key !== "discount_amount" &&
                        parseFloat(charges[key]) > 0 ? (
                        <div
                          key={index}
                          className="flex items-center justify-between"
                        >
                          <span className="text-gray-600 flex items-center text-sm sm:text-base">
                            <FontAwesomeIcon
                              icon={faPaw}
                              className="text-yellow-500 mr-2 text-xs sm:text-sm"
                            />
                            {getServiceLabel(key)}:
                          </span>
                          <span className="text-gray-800 text-sm sm:text-base">
                            <strong>
                              {parseFloat(charges[key]).toFixed(2)}{" "}
                              {CURRENCY_CODE}
                            </strong>
                          </span>
                        </div>
                      ) : null
                    )}
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t mt-3">
                    <span className="text-gray-600 flex items-center text-sm sm:text-base">
                      <FontAwesomeIcon
                        icon={faPaw}
                        className="text-tblue mr-2 text-xs sm:text-sm"
                      />{" "}
                      Base Rate:
                    </span>
                    <span className="text-gray-800 text-sm sm:text-base">
                      <strong>
                        +{parseFloat(walk.charges.base_rate).toFixed(2)}{" "}
                        {CURRENCY_CODE}
                      </strong>
                    </span>
                  </div>
                  {parseFloat(walk.charges.discount_amount) > 0 && (
                    <div className="flex items-center justify-between text-green-600 mt-2">
                      <span className="flex items-center text-sm sm:text-base">
                        <FontAwesomeIcon
                          icon={faPaw}
                          className="text-green-500 mr-2 text-xs sm:text-sm"
                        />{" "}
                        Discount Applied:
                      </span>
                      <strong className="text-sm sm:text-base">
                        -{parseFloat(walk.charges.discount_amount).toFixed(2)}{" "}
                        {CURRENCY_CODE}
                      </strong>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-base sm:text-lg font-bold border-t pt-3 mt-4">
                    <span className="text-purple-600 flex items-center text-sm sm:text-base">
                      <FontAwesomeIcon icon={faPaw} className="mr-2 text-xs sm:text-sm" />
                      Total{" "}
                      {parseFloat(walk.charges.discount_amount) > 0
                        ? "(After Discount)"
                        : ""}
                      :
                    </span>
                    <span className="text-gray-800 text-sm sm:text-base">
                      <strong>
                        {parseFloat(walk.charges.total).toFixed(2)}{" "}
                        {CURRENCY_CODE}
                      </strong>
                    </span>
                  </div>
                </div>

                {/* Pets and Tools Section */}
                {pets && pets.length > 0 && (
                  <div className="mt-4 sm:mt-6">
                    <h4 className="text-gray-700 font-semibold text-sm sm:text-base">Pets</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mt-2">
                      {pets.map((pet) => (
                        <motion.div
                          key={pet.id}
                          className="bg-gray-100 p-2 sm:p-3 rounded-lg shadow-md text-center"
                          whileHover={{ scale: 1.05 }}
                        >
                          <img
                            src={pet.primary_image_url}
                            alt={pet.name}
                            className="w-12 h-12 sm:w-16 sm:h-16 rounded-full mx-auto object-cover mb-2"
                          />
                          <h5 className="text-gray-800 font-semibold text-sm sm:text-base">
                            {pet.name}
                          </h5>
                          <p className="text-xs sm:text-sm text-gray-500">
                            {pet.type.name}
                          </p>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {tools && tools.length > 0 && (
                  <div className="mt-4 sm:mt-6">
                    <h4 className="text-gray-700 font-semibold text-sm sm:text-base">Tools</h4>
                    <div className="grid grid-cols-1 gap-2 sm:gap-3 mt-2">
                      {tools.map((tool) => (
                        <div
                          key={tool.id}
                          className="flex justify-between items-center bg-gray-50 p-2 sm:p-3 rounded-lg"
                        >
                          <span className="font-semibold text-gray-800 text-sm sm:text-base">
                            {tool.name}
                          </span>
                          <span className="text-gray-600 text-sm sm:text-base">{tool.price + " " + CURRENCY_CODE}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Additional Information */}
                <EditAdditionalInfo
                  item={walk}
                  type="walks"
                  onSave={handleAdditionalInfoSave}
                  className="mt-6"
                  canEdit={canEditAdditionalDetail}
                />

                {/* Close Button */}
                <div className="text-center mt-6 sm:mt-8">
                  <button
                    onClick={onClose}
                    className="px-3 sm:px-4 py-2 sm:py-2.5 bg-purple-600 text-white rounded-lg shadow-md hover:bg-purple-700 transition-transform transform text-sm sm:text-base w-full sm:w-auto"
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default WalkDetailModal;
